"use client"

import { useEffect, useState, type ComponentType } from "react"
import { deferAuthStoreMutation, useAuthStore } from "@/features/auth/store"

function hasPersistedAuthSession(): boolean {
  return document.cookie
    .split(";")
    .some((cookie) => cookie.trim().startsWith("auth-token"))
}

/** Keeps the auth SDK out of anonymous startup and loads it on first intent. */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [AuthRuntime, setAuthRuntime] = useState<ComponentType | null>(null)

  useEffect(() => {
    let cancelled = false
    let started = false

    const startAuth = async () => {
      if (started) return
      started = true
      window.removeEventListener("pointerdown", startAuth)
      window.removeEventListener("keydown", startAuth)

      const { AuthRuntime: LoadedAuthRuntime } = await import("./auth-runtime")
      if (!cancelled) setAuthRuntime(() => LoadedAuthRuntime)
    }

    if (hasPersistedAuthSession()) {
      void startAuth()
      return () => {
        cancelled = true
      }
    }

    deferAuthStoreMutation(() => useAuthStore.getState().setLoading(false))
    window.addEventListener("pointerdown", startAuth, { once: true })
    window.addEventListener("keydown", startAuth, { once: true })
    return () => {
      cancelled = true
      window.removeEventListener("pointerdown", startAuth)
      window.removeEventListener("keydown", startAuth)
    }
  }, [])

  return (
    <>
      {children}
      {AuthRuntime ? <AuthRuntime /> : null}
    </>
  )
}
